import { useRef, useState } from 'react';
import type { ChangeEvent, SyntheticEvent, DragEvent } from 'react';
import type { ImageValidationResult } from '~/modules/file/util/validate-images';
import { validateImages } from '~/modules/file/util/validate-images';
import {
    ALLOWED_FILE_TYPES,
    ALLOWED_IMAGE_TYPES,
    MAX_UPLOAD_FILE_NUMBER,
    MAX_UPLOAD_FILE_SIZE,
    MAX_UPLOAD_IMAGE_SIZE,
    MIN_UPLOAD_IMAGE_HEIGHT,
    MIN_UPLOAD_IMAGE_WIDTH,
} from '~/modules/design-board/config';
import { validateFiles } from '~/modules/file/util/validate-files';
import { useBoolean } from 'ahooks';
import { useDesignBoardPageState } from '~/modules/design-board/component/context';
import { useNavigate } from '@remix-run/react';
import { useUrlPinTargetType } from '~/modules/design-board/hook/use-url-pin-target-type';
import { DesignBoardPinTargetType } from 'generated/graphql';
import LoginSignUpModal from '~/modules/user/component/login-signup-modal/LoginSignUpModal';
import { isUserLoggedIn, useUser } from '~/modules/user';
import { useTracker } from '@archipro-website/tracker';
import { CustomArrowToTop } from '@archipro-design/icons';
import { INVALID_CHARS_FOR_FILENAME } from '~/utils/uploadHelper';
import { Button as RoccoButton } from '@archipro/rocco/components/button';

interface UploadPinOptionProps {
    uploadResultRoute?: string;
    callbackFn?: (results: ImageValidationResult[]) => void;
    mobile: boolean;
}

export const useUploadPin = ({
    callbackFn,
    uploadResultRoute,
    mobile,
}: UploadPinOptionProps) => {
    const inputFile = useRef<HTMLInputElement | null>(null);
    const [uploadErrorMsg, setUploadErrorMsg] = useState('');
    const [isDragActive, dragAction] = useBoolean();
    const { dispatch } = useDesignBoardPageState();
    const navigate = useNavigate();
    const urlPinType = useUrlPinTargetType();
    const tracker = useTracker();

    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const [openLoginModal, setOpenLoginModal] = useState(false);

    const onUploadClick = (e: SyntheticEvent) => {
        if (!isLoggedIn) setOpenLoginModal(true);
        else {
            e.preventDefault();
            inputFile.current?.click();
            tracker.log('UploadReveal', {
                url: new URL(window.location.href),
                data: {
                    ElementID: 'designboard_detail_header_uploadReveal',
                    Source: 'designBoard, detail, header',
                },
            });
        }
    };

    const submitUpload = (results: ImageValidationResult[]) => {
        if (!mobile) {
            dispatch({
                type: 'UploadPin',
                uploadFiles: results,
            });
        } else {
            urlPinType === DesignBoardPinTargetType.MemberUpload &&
                dispatch({
                    type: 'UploadPin',
                    uploadFiles: results,
                });
        }
        uploadResultRoute && navigate(uploadResultRoute);
        callbackFn && callbackFn(results);
    };

    const handleUploadFiles = async (files: FileList) => {
        const filesArray = [...files] as File[];

        if (files.length > MAX_UPLOAD_FILE_NUMBER) {
            submitUpload([
                {
                    valid: false,
                    message: `Upload files number must not exceed ${MAX_UPLOAD_FILE_NUMBER}`,
                },
            ]);
            return false;
        }

        //validate individually
        const validations = filesArray.map(async (file) => {
            const isImage = file.type.startsWith('image/');
            if (isImage && file.size > MAX_UPLOAD_IMAGE_SIZE * 1024 * 1024) {
                return {
                    valid: false,
                    message: `Image file size must not exceed ${MAX_UPLOAD_IMAGE_SIZE}MB`,
                };
            }

            return isImage
                ? validateImages({
                      files: [file],
                      validationOptions: {
                          uploadLimit: { max: MAX_UPLOAD_FILE_NUMBER },
                          fileExtensions: ALLOWED_IMAGE_TYPES,
                          invalidChars: INVALID_CHARS_FOR_FILENAME,
                          imageWidth: {
                              min: MIN_UPLOAD_IMAGE_WIDTH,
                          },
                          imageHeight: {
                              min: MIN_UPLOAD_IMAGE_HEIGHT,
                          },
                      },
                  })
                : validateFiles({
                      files: [file],
                      validationOptions: {
                          uploadLimit: { max: MAX_UPLOAD_FILE_NUMBER },
                          fileExtensions: ALLOWED_FILE_TYPES,
                          fileSizeMB: { max: MAX_UPLOAD_FILE_SIZE },
                      },
                  });
        });

        // show error message first
        const validatedFiles = await Promise.all(validations);
        const validFilesFirst = validatedFiles.sort(
            (a, b) => Number(a.valid) - Number(b.valid)
        );

        submitUpload(validFilesFirst);
    };

    const handleInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        e.preventDefault();
        if (target.files?.length) {
            await handleUploadFiles(target.files);
        }
    };

    const hiddenInput = (
        <input
            type="file"
            id="file"
            ref={inputFile}
            style={{ display: 'none' }}
            onChange={handleInputChange}
            multiple={MAX_UPLOAD_FILE_NUMBER > 1}
        />
    );

    const uploadButton = () => (
        <>
            <RoccoButton color="dark" size="default" onClick={onUploadClick}>
                <CustomArrowToTop className="mr-2 h-3 w-3" />
                Upload
            </RoccoButton>
            {hiddenInput}
            <LoginSignUpModal
                login={{
                    onCancel: () => setOpenLoginModal(false),
                    onAuthSuccess: () => setOpenLoginModal(false),
                    open: openLoginModal,
                    flow: null,
                    authSource: 'designBoard',
                }}
                forgotPassword={{}}
            />
        </>
    );

    const handleDrag = (e: DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        e.stopPropagation();

        if (e.type === 'dragenter' || e.type === 'dragover') {
            dragAction.setTrue();
        } else if (e.type === 'dragleave') {
            dragAction.setFalse();
        }
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        e.stopPropagation();

        dragAction.setFalse();

        if (e.dataTransfer.files.length) {
            handleUploadFiles(e.dataTransfer.files);
        }
    };

    return {
        handleUploadFiles,
        uploadButton,
        onUploadClick,
        uploadErrorMsg,
        setUploadErrorMsg,
        hiddenInput,
        handleDrag,
        handleDrop,
        isDragActive,
    };
};
